import React, { useState, useEffect } from 'react';
import Order from '../components/Order.js';
import axios from 'axios';
import ResponsiveContainer from '../components/ResponsiveContainer';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../config/theme.js';
import Typography from '@material-ui/core/Typography';
import SvgIcon from '@material-ui/core/SvgIcon';
import { makeStyles } from '@material-ui/core/styles';
import InfoMessage from '../components/InfoMessage';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles((theme) => ({
  main: {
    minHeight: '620px',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  lightningIcon: {
    position: 'relative',
    filter: 'brightness(0) saturate(100%) invert(16%) sepia(97%) saturate(2586%) hue-rotate(222deg) brightness(89%) contrast(101%)',
    fontSize: '1.45rem',
    flexShrink: 0,
    display: 'block',
    alignSelf: 'center',
  },
  title: {
    fontWeight: 700,
    marginBottom: 0,
    color: '#1A1A1A',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '170px',
    height: '34px',
    borderRadius: '8px',
    border: '1px solid #D9DDE5',
    backgroundColor: '#EAF0FB',
    color: '#2A5B2C',
    padding: '0 10px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  searchIcon: {
    color: '#6E916F',
    fontSize: '1.1rem',
    flexShrink: 0,
  },
  searchBox: {
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#2A5B2C',
    fontSize: '0.875rem',
    width: '100%',
    '&::placeholder': {
      color: '#6E916F',
    },
  },
  warningBox: {
    backgroundColor: '#EEF2F8',
    border: '1px solid #E0E6F0',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '14px',
    color: '#2A482B',
    fontSize: '0.93rem',
    lineHeight: 1.45,
  },
  warningTitle: {
    fontWeight: 700,
    marginBottom: '4px',
  },
  warningClose: {
    float: 'right',
    cursor: 'pointer',
    fontWeight: 700,
    color: '#254A26',
    border: 'none',
    background: 'transparent',
  },
  button: {
    color: '#3B7E3D',
    borderRadius: '8px',
    border: '2px solid #3B7E3D',
    backgroundColor: 'transparent',
    padding: '8px 20px',
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(59, 126, 61, 0.08)',
    },
  },
  orders: {
    paddingTop: '6px',
  },
  info: {
    color: '#666666',
    margin: '40px',
    textAlign: 'center',
    fontSize: '110%',
    fontWeight: 600
  },
}));

export default function OrdersIndex({ currentUser, influencer }) {
  const classes = useStyles();
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [finalPage, setFinalPage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWarning, setShowWarning] = useState(true);

  if (!currentUser)
    window.location.href = '/public/sign_in';

  async function fetchOrders(page) {
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/purchase_orders/${influencer.username}/index?page=${page}&per_page=${perPage}`);
      if (response.data.length != perPage)
        setFinalPage(true);
      setOrders(orders.concat(response.data));
    } catch (error) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }

  function loadMore() {
    fetchOrders(currentPage + 1);
    setCurrentPage(currentPage + 1);
  }

  useEffect(() => {
    fetchOrders(currentPage);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <ResponsiveContainer>
        <div className={classes.main}>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              <SvgIcon className={classes.lightningIcon} viewBox="0 0 24 24">
                <path d="M22 10V6c0-1.11-.9-2-2-2H4c-1.1 0-1.99.89-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-9 7.5h-2v-2h2v2zm0-4.5h-2v-2h2v2zm0-4.5h-2v-2h2v2z"></path>
              </SvgIcon>
              Meus Títulos
            </Typography>
            <div className={classes.searchWrapper}>
              <SearchIcon className={classes.searchIcon} />
              <input
                className={classes.searchBox}
                placeholder="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {showWarning && (
            <div className={classes.warningBox}>
              <button className={classes.warningClose} onClick={() => setShowWarning(false)}>×</button>
              <div className={classes.warningTitle}>Atenção:</div>
              <div>🚫 Não realizamos chamadas de vídeo.</div>
              <div>🚫 Não cobramos valores para liberar prêmios.</div>
              <div style={{ marginTop: '6px' }}>
                Se alguém pedir qualquer uma dessas ações, fique atento. <b>Pode se tratar de uma tentativa de fraude.</b>
              </div>
            </div>
          )}
          <div className={classes.orders}>
            {!loading && orders
              .filter((order) => `${order?.campaign?.title || ''}`.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((order) => (
              <Order key={order.id} order={order} />
            ))}
            {loading && <div className={classes.info}>Carregando...</div>}
            {!loading && orders.length == 0 && <InfoMessage>Nenhum pedido encontrado</InfoMessage>}
          </div>
          <div>
            {!finalPage && !searchTerm && <button disabled={loading} className={classes.button} onClick={loadMore}>Carregar mais</button>}
          </div>
        </div>
      </ResponsiveContainer>
    </ThemeProvider>
  );
};